// Minimal in-memory replacement for Node's `fs` module, exposing only the
// synchronous calls used by src/mavlink/mavlink_ftp.js.
//
// The aim is to allow MAVLink FTP downloads (vehicle -> browser) to complete
// in pure browser: data accumulated in-memory and offered as a download once
// the FTP session is terminated.
//
// File handles are integers indexing into an internal map. Each handle owns
// a growable Uint8Array buffer (for 'w+') or a frozen Uint8Array (for 'r').

const Buffer = require('buffer').Buffer;

const handles = new Map();
let nextHandle = 1;
// Pre-registered read-only files keyed by a virtual filename. Populated
// by `registerFile()` from the renderer (e.g. mavFtpPut after the user
// picks a file via <input type="file">). When openSync() is called with
// a 'r' flag matching one of these names the bytes are made available
// to readSync().
const pendingFiles = new Map();

function asUint8(buffer) {
    if (buffer instanceof Uint8Array) return buffer;
    if (buffer && typeof buffer.byteLength === 'number') return new Uint8Array(buffer);
    return new Uint8Array(buffer || []);
}

function ensureCapacity(entry, requiredEnd) {
    if (entry.data.length >= requiredEnd) return;
    let newSize = entry.data.length || 1024;
    while (newSize < requiredEnd) newSize *= 2;
    const grown = new Uint8Array(newSize);
    grown.set(entry.data);
    entry.data = grown;
}

function downloadEntry(entry) {
    if (!entry || entry.size <= 0) return;
    const slice = entry.data.slice(0, entry.size);
    const blob = new Blob([slice], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = entry.name || 'download.bin';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function openSync(filename, flags) {
    const handle = nextHandle++;
    const mode = flags || 'r';
    const name = (filename || 'download.bin').split('/').pop();
    let data = new Uint8Array(0);
    let size = 0;
    // For read-only opens, see if the file has been pre-registered
    // (browser-mode uploads stash the user-picked File bytes here).
    if (mode.indexOf('w') === -1 && pendingFiles.has(filename)) {
        data = pendingFiles.get(filename);
        size = data.length;
    }
    const entry = {
        name,
        mode,
        data,
        size,
    };
    handles.set(handle, entry);
    return handle;
}

function registerFile(filename, data) {
    pendingFiles.set(filename, asUint8(data));
}

function unregisterFile(filename) {
    pendingFiles.delete(filename);
}

function fstatSync(handle) {
    const entry = handles.get(handle);
    if (!entry) throw new Error('Invalid file handle');
    return { size: entry.size };
}

function writeSync(handle, buffer, offset, length, position) {
    const entry = handles.get(handle);
    if (!entry) throw new Error('Invalid file handle');
    const src = asUint8(buffer);
    const writeFrom = offset || 0;
    const writeLen = (typeof length === 'number') ? length : (src.length - writeFrom);
    const writeAt = (typeof position === 'number') ? position : entry.size;
    ensureCapacity(entry, writeAt + writeLen);
    entry.data.set(src.subarray(writeFrom, writeFrom + writeLen), writeAt);
    if (writeAt + writeLen > entry.size) entry.size = writeAt + writeLen;
    return writeLen;
}

function readSync(handle, buffer, offset, length, position) {
    const entry = handles.get(handle);
    if (!entry) throw new Error('Invalid file handle');
    const readAt = position || 0;
    const available = Math.max(0, entry.size - readAt);
    const toRead = Math.min(length, available);
    const dst = asUint8(buffer);
    dst.set(entry.data.subarray(readAt, readAt + toRead), offset || 0);
    return toRead;
}

function closeSync(handle) {
    const entry = handles.get(handle);
    if (!entry) return;
    // For write handles, trigger a browser download of the captured data.
    if (entry.mode && entry.mode.indexOf('w') !== -1) {
        downloadEntry(entry);
    }
    handles.delete(handle);
}

// Stash so a caller (mavlink_ftp `terminateSession`) can flush a still-open
// handle without an explicit closeSync.
function _flushHandle(handle) {
    const entry = handles.get(handle);
    if (entry && entry.mode && entry.mode.indexOf('w') !== -1) {
        downloadEntry(entry);
        // Reset so we do not flush twice if closeSync is called later.
        entry.mode = 'r';
        entry.size = 0;
        entry.data = new Uint8Array(0);
    }
}

module.exports = {
    openSync,
    fstatSync,
    writeSync,
    readSync,
    closeSync,
    _flushHandle,
    _handles: handles,
    registerFile,
    unregisterFile,
};
